import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { enqueueSnackbar } from 'notistack'
import _ from 'lodash'
import moment from 'moment-timezone'
import isEmail from 'validator/lib/isEmail'
import isURL from 'validator/lib/isURL'
import { HexColorPicker } from 'react-colorful'

import { AppContext } from '/providers/app-context'
import { getTemplateData } from '/providers/templates-api'
import { updatePassVariables } from '/providers/studio-api'
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'

import currencies from '/utils/currencies.json'
import { hasRequiredVariableValue, isRequiredVariableForTemplate } from '/utils/variable-required'

import {
  Alert,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import CloseIcon from '@mui/icons-material/Close'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'

const formatValueForInput = value => {
  if (_.isPlainObject(value) || Array.isArray(value)) return JSON.stringify(value, null, 2)
  if (value == null) return ""
  return String(value)
}

const isIntegerInput = input => /^-?\d*$/.test(input)
const isDecimalInput = input => /^-?\d*(\.\d*)?$/.test(input)
const isValidImageUrlInput = input => {
  if (!isURL(input, { protocols: ['http', 'https'], require_protocol: true })) return false

  try {
    const { pathname } = new URL(input)
    return /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i.test(pathname)
  }
  catch {
    return false
  }
}
const isValidColourInput = input => /^#?[0-9a-fA-F]{6}$/.test(input)
const normaliseColourInput = input => /^#[0-9a-fA-F]{6}$/.test(input)
  ? input
  : /^[0-9a-fA-F]{6}$/.test(input)
    ? `#${input}`
    : null
const isValidDateInput = input => /^\d{8}$/.test(input) && moment(input, "YYYYMMDD", true).isValid()
const isValidDateTimeInput = input => /^\d{14}$/.test(input) && moment(input, "YYYYMMDDHHmmss", true).isValid()
const isValidLocationInput = input => {
  if (typeof input !== "string") return false

  const parts = input.split(",").map(part => part.trim())
  if (parts.length !== 2 || parts.some(part => part === "")) return false

  const [latitude, longitude] = parts.map(Number)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false

  return latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180
}
const isValidCurrencyCodeInput = input => /^[A-Z]{3}$/.test(input ?? "")
  && currencies.some(currency => currency.code === input)
const isColourSubType = varSubType => ["colour", "color", "colours", "colors"].includes(varSubType)
const isNumberSubType = varSubType => ["integer", "number", "money"].includes(varSubType)

const validateValueInput = ({ input, varSubType, required = false, label = "Value" }) => {
  const value = input ?? ""
  if (!hasRequiredVariableValue(value)) {
    return required
      ? { valid: false, helperText: `${label} is required.` }
      : { valid: true, helperText: "" }
  }

  switch (varSubType) {
    case "email":
      return {
        valid: isEmail(value),
        helperText: isEmail(value) ? "" : "Enter a valid email address."
      }
    case "www":
    case "link":
      return {
        valid: isURL(value, { protocols: ['http', 'https'], require_protocol: true }),
        helperText: isURL(value, { protocols: ['http', 'https'], require_protocol: true }) ? "" : "Enter a valid URL starting with http:// or https://."
      }
    case "image":
      return {
        valid: isValidImageUrlInput(value),
        helperText: isValidImageUrlInput(value) ? "" : "Enter a valid http:// or https:// image URL ending in an image file extension."
      }
    case "number":
    case "money":
      return {
        valid: isDecimalInput(value),
        helperText: isDecimalInput(value) ? "" : "Enter a valid number."
      }
    case "date":
      return {
        valid: isValidDateInput(value),
        helperText: isValidDateInput(value) ? "" : "Use YYYYMMDD and enter a valid date."
      }
    case "datetime":
      return {
        valid: isValidDateTimeInput(value),
        helperText: isValidDateTimeInput(value) ? "" : "Use YYYYMMDDHHmmss and a valid 24-hour time."
      }
    case "location":
      return {
        valid: isValidLocationInput(value),
        helperText: isValidLocationInput(value) ? "" : "Use latitude,longitude with valid ranges."
      }
    case "boolean":
      return {
        valid: ["true", "false"].includes(value),
        helperText: ["true", "false"].includes(value) ? "" : "Choose true or false."
      }
    case "colour":
    case "color":
    case "colours":
    case "colors":
      return {
        valid: isValidColourInput(value),
        helperText: isValidColourInput(value) ? "" : "Use a 6-digit hex colour such as #A1B2C3."
      }
    case "choice":
      return {
        valid: isValidCurrencyCodeInput(value),
        helperText: isValidCurrencyCodeInput(value) ? "" : "Use a valid 3-letter ISO currency code such as GBP."
      }
    default:
      return { valid: true, helperText: "" }
  }
}

const parseValueFromInput = ({ input, previousValue, varSubType }) => {
  if (_.isPlainObject(previousValue) || Array.isArray(previousValue)) {
    try {
      return { ok: true, value: JSON.parse(input) }
    }
    catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : "Invalid JSON" }
    }
  }

  if (typeof previousValue === "number" || ["integer", "number", "money"].includes(varSubType)) {
    if (input === "") return { ok: true, value: "" }
    const parsed = Number(input)
    return { ok: true, value: Number.isNaN(parsed) ? input : parsed }
  }

  if (typeof previousValue === "boolean" || varSubType === "boolean") {
    return { ok: true, value: input === "true" }
  }

  if (varSubType === "choice") {
    return { ok: true, value: input?.toString().trim().toUpperCase() ?? input }
  }

  if (isColourSubType(varSubType)) {
    return { ok: true, value: normaliseColourInput(input) ?? input }
  }

  return { ok: true, value: input }
}

const toNativeDateValue = value => value && isValidDateInput(value) ? moment(value, "YYYYMMDD").format("YYYY-MM-DD") : ""
const toNativeDateTimeValue = value => value && isValidDateTimeInput(value) ? moment(value, "YYYYMMDDHHmmss").format("YYYY-MM-DDTHH:mm:ss") : ""
const fromNativeDateValue = value => value ? moment(value, "YYYY-MM-DD").format("YYYYMMDD") : ""
const fromNativeDateTimeValue = value => value ? moment(value, "YYYY-MM-DDTHH:mm:ss").format("YYYYMMDDHHmmss") : ""

const isRowReadOnly = item => {
  if (!item) return false
  return Number(item.patch_enabled) !== 1
}

const unsetPathAndPrune = (obj, path) => {
  _.unset(obj, path)
  const parts = path.split(".")

  for (let i = parts.length - 1; i > 0; i -= 1) {
    const parentPath = parts.slice(0, i).join(".")
    const parentValue = _.get(obj, parentPath)

    if (_.isPlainObject(parentValue) && Object.keys(parentValue).length === 0) {
      _.unset(obj, parentPath)
    } else {
      break
    }
  }
}

const hasVariablePath = (obj, path) => _.has(obj, path) || Object.prototype.hasOwnProperty.call(obj ?? {}, path)

const getVariablePathValue = (obj, path) => {
  if (_.has(obj, path)) return _.get(obj, path)
  if (Object.prototype.hasOwnProperty.call(obj ?? {}, path)) return obj[path]
  return undefined
}

const setVariablePathValue = (obj, path, value) => {
  if (Object.prototype.hasOwnProperty.call(obj ?? {}, path)) {
    obj[path] = value
    return
  }

  _.set(obj, path, value)
}

const unsetVariablePath = (obj, path) => {
  if (Object.prototype.hasOwnProperty.call(obj ?? {}, path)) {
    delete obj[path]
    return
  }

  unsetPathAndPrune(obj, path)
}

const coerceValueByVarType = ({ value, varType }) => {
  if (value === null || value === undefined) return value

  if (varType === "boolean") {
    if (typeof value === "boolean") return value
    if (typeof value === "number") return value !== 0
    if (typeof value === "string") {
      const normalised = value.trim().toLowerCase()
      if (normalised === "true") return true
      if (normalised === "false") return false
    }
    return value
  }

  if (varType === "integer") {
    if (typeof value === "number") return Math.trunc(value)
    if (typeof value === "string") {
      const trimmed = value.trim()
      if (trimmed === "") return value
      const parsed = Number.parseInt(trimmed, 10)
      return Number.isNaN(parsed) ? value : parsed
    }
    return value
  }

  if (isNumberSubType(varType)) {
    if (typeof value === "number") return value
    if (typeof value === "string") {
      const trimmed = value.trim()
      if (trimmed === "") return value
      const parsed = Number(trimmed)
      return Number.isNaN(parsed) ? value : parsed
    }
    return value
  }

  return value
}

const buildOverridePayloadFromRows = (rows, sourceVariables) => {
  const payload = {}

  rows.forEach(row => {
    if (!row.included) return
    const value = coerceValueByVarType({
      value: getVariablePathValue(sourceVariables, row.varName),
      varType: row.item?.var_type
    })
    setVariablePathValue(payload, row.varName, value)
  })

  return payload
}

const EditForm = ({ pass, templateId, setPass, dataSource = "variables" }) => {
  const { client, apiSource, apiKey, setProcessing } = useContext(AppContext)
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  const [limitedUpdates, setLimitedUpdates] = useState(false)
  const [loadingTemplateData, setLoadingTemplateData] = useState(false)
  const [templateVariables, setTemplateVariables] = useState({})
  const [catalogItems, setCatalogItems] = useState([])
  const [isManagedPassTemplate, setIsManagedPassTemplate] = useState(false)
  const [originalVariables, setOriginalVariables] = useState({})
  const [draftVariables, setDraftVariables] = useState({})
  const [rowInputs, setRowInputs] = useState({})
  const [rowErrors, setRowErrors] = useState({})
  const [infoMessage, setInfoMessage] = useState("")

  useEffect(() => {
    if (!pass) return

    const sourceData = _.cloneDeep(pass[dataSource] ?? {})
    setOriginalVariables(sourceData)
    setDraftVariables(_.cloneDeep(sourceData))
    setRowInputs({})
    setRowErrors({})
    setInfoMessage("")

    if (client.limitUpdates && pass.update_counter >= client.updates_per_day) {
      setLimitedUpdates(true)
    } else {
      setLimitedUpdates(false)
    }
  }, [pass, client.limitUpdates, client.updates_per_day, dataSource])

  useEffect(() => {
    let active = true

    const loadTemplateData = async () => {
      if (!templateId) return
      setLoadingTemplateData(true)

      const result = await getTemplateData({ apiSource, apiKey, templateId })

      if (!active) return

      if (result?.error || result?.ok === false) {
        setLoadingTemplateData(false)
        showActionError(result?.error, "Failed to load template variable catalog")
        return
      }

      setTemplateVariables(result?.template?.variables ?? {})
      setCatalogItems(result?.template?.variable_catalog ?? [])
      setIsManagedPassTemplate(Number(result?.template?.managed_pass_template) === 1)
      setLoadingTemplateData(false)
    }

    loadTemplateData()

    return () => {
      active = false
    }
  }, [apiKey, apiSource, templateId])

  const rows = useMemo(() => {
    return _.sortBy(catalogItems, ["var_name"]).map(item => {
      const varName = item.var_name
      const included = hasVariablePath(draftVariables, varName)
      const templateValue = getVariablePathValue(templateVariables, varName)
      const draftValue = included ? getVariablePathValue(draftVariables, varName) : undefined
      const valueInput = rowInputs[varName] ?? formatValueForInput(draftValue)
      const valueError = rowErrors[varName] ?? ""

      return {
        item,
        varName,
        included,
        templateValue,
        draftValue,
        valueInput,
        valueError,
        readOnly: isRowReadOnly(item),
      }
    })
  }, [catalogItems, draftVariables, rowInputs, rowErrors, templateVariables])

  const hasValidationErrors = useMemo(() => Object.values(rowErrors).some(Boolean), [rowErrors])
  const hasChanges = useMemo(() => !_.isEqual(originalVariables, draftVariables), [originalVariables, draftVariables])

  const setDraftValue = (varName, value) => {
    setDraftVariables(current => {
      const next = _.cloneDeep(current)
      setVariablePathValue(next, varName, value)
      return next
    })
  }

  const toggleOverride = (row, checked) => {
    setInfoMessage("")

    if (checked) {
      const seedValue = hasVariablePath(draftVariables, row.varName)
        ? getVariablePathValue(draftVariables, row.varName)
        : getVariablePathValue(templateVariables, row.varName)

      const initialValue = seedValue === undefined ? "" : seedValue
      setDraftValue(row.varName, initialValue)
      setRowInputs(current => ({ ...current, [row.varName]: formatValueForInput(initialValue) }))

      const validation = validateValueInput({
        input: formatValueForInput(initialValue),
        varSubType: row.item?.var_type,
        required: isRequiredVariableForTemplate({
          catalogEntry: row.item,
          isManagedPassTemplate
        }),
        label: row.varName
      })
      setRowErrors(current => ({ ...current, [row.varName]: validation.valid ? "" : validation.helperText }))
      return
    }

    setDraftVariables(current => {
      const next = _.cloneDeep(current)
      unsetVariablePath(next, row.varName)
      return next
    })

    setRowInputs(current => _.omit(current, [row.varName]))
    setRowErrors(current => _.omit(current, [row.varName]))
  }

  const updateOverrideValue = (row, nextInputRaw) => {
    const varSubType = row.item?.var_type
    const nextInput = varSubType === "choice"
      ? nextInputRaw?.toString().toUpperCase()
      : nextInputRaw

    if (varSubType === "integer" && !isIntegerInput(nextInput)) return
    if (["number", "money"].includes(varSubType) && !isDecimalInput(nextInput)) return
    if (varSubType === "choice" && !/^[A-Z]{0,3}$/.test(nextInput)) return

    setRowInputs(current => ({ ...current, [row.varName]: nextInput }))

    const validation = validateValueInput({
      input: nextInput,
      varSubType,
      required: isRequiredVariableForTemplate({
        catalogEntry: row.item,
        isManagedPassTemplate
      }),
      label: row.varName
    })

    setRowErrors(current => ({ ...current, [row.varName]: validation.valid ? "" : validation.helperText }))
    if (!validation.valid) return

    const currentValue = getVariablePathValue(draftVariables, row.varName)
    const parsed = parseValueFromInput({
      input: nextInput,
      previousValue: currentValue,
      varSubType
    })

    if (!parsed.ok) {
      setInfoMessage(`Invalid JSON for ${row.varName}: ${parsed.error}`)
      return
    }

    setInfoMessage("")
    setDraftValue(row.varName, parsed.value)
  }

  const resetTextOverride = row => {
    if (row.readOnly) return
    updateOverrideValue(row, "")
  }

  const save = async () => {
    if (!hasChanges || hasValidationErrors) return

    const missingRequiredRows = rows.filter(row => {
      if (!row.included) return false
      if (!isRequiredVariableForTemplate({ catalogEntry: row.item, isManagedPassTemplate })) return false
      return !hasRequiredVariableValue(getVariablePathValue(draftVariables, row.varName))
    })

    if (missingRequiredRows.length) {
      setInfoMessage(`Required values missing: ${missingRequiredRows.map(row => row.varName).join(", ")}`)
      return
    }

    const payloadOverrides = buildOverridePayloadFromRows(rows, draftVariables)

    setProcessing(true)
    enqueueSnackbar({ message: 'Saving pass variables', variant: 'light' })
    const result = await updatePassVariables({ apiSource, apiKey, templateId, passId: pass.pass_id, variables: payloadOverrides })

    if (result.pass) {
      enqueueSnackbar({ message: 'Pass variables saved', variant: 'success' })
      setPass(result.pass)
      const updated = _.cloneDeep(result.pass[dataSource] ?? {})
      setOriginalVariables(updated)
      setDraftVariables(_.cloneDeep(updated))
      setRowInputs({})
      setRowErrors({})
      setInfoMessage("")
    } else {
      showActionError(result.error, "Unknown error")
    }

    setProcessing(false)
  }

  const columns = useMemo(() => ([
    {
      accessorKey: 'varName',
      header: 'Variable',
      size: 260,
      Cell: ({ row }) => row.original.varName
    },
    {
      accessorKey: 'templateValue',
      header: 'Template value',
      size: 340,
      Cell: ({ row }) => (
        <TextField
          size='small'
          fullWidth
          disabled
          value={formatValueForInput(row.original.templateValue)}
          placeholder={row.original.templateValue == null ? "NULL" : ""}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      )
    },
    {
      accessorKey: 'included',
      header: 'Pass override',
      size: 140,
      Cell: ({ row }) => (
        <div className='flex justify-center'>
          <Switch
            checked={row.original.included}
            disabled={row.original.readOnly}
            onChange={event => toggleOverride(row.original, event.target.checked)}
          />
        </div>
      )
    },
    {
      accessorKey: 'valueInput',
      header: 'Pass value',
      size: 420,
      Cell: ({ row }) => (
        row.original.included ? (
          <SubtypeValueField
            fullWidth
            size='small'
            disabled={row.original.readOnly}
            value={row.original.valueInput}
            varSubType={row.original.item?.var_type}
            error={Boolean(row.original.valueError)}
            helperText={row.original.valueError}
            onChange={nextValue => updateOverrideValue(row.original, nextValue)}
            onReset={row.original.item?.var_type === "text" ? () => resetTextOverride(row.original) : null}
          />
        ) : (
          <Typography variant='body2' color='text.secondary'>Override disabled</Typography>
        )
      )
    }
  ]), [toggleOverride, updateOverrideValue, resetTextOverride])

  const table = useMaterialReactTable({
    columns,
    data: rows,
    enablePagination: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableFilters: false,
    enableGlobalFilter: false,
    enableSorting: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnResizing: true,
    layoutMode: 'grid',
    muiTableContainerProps: {
      sx: {
        height: '100%',
        maxHeight: '100%',
      }
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'transparent'
      }
    }
  })

  if (limitedUpdates) return (
    <div className='flex flex-col gap-5 pt-10'>
      <div>
        This pass has been edited and pushed to devices or a notification sent recently. Please check back later.
      </div>

      <div>
        Since you are using Smartix signing certifiates, you are limited to one pass update per 24 hours.
      </div>

      <div>
        If you require more frequent pass updates / notifications then please register your own Apple and Google developer accounts
        and provide us with your own signing certificates.
      </div>
    </div>
  )

  if (loadingTemplateData) {
    return (
      <div className='flex h-full items-center justify-center'>
        <Typography>Loading template variables...</Typography>
      </div>
    )
  }

  return (
    <>
      <div className='flex h-full min-h-0 flex-col gap-3 pt-4'>
        <Typography variant='body2'>
          Template values are shown for reference. Enable a checkbox to override a variable for this pass.
        </Typography>

        <div className='flex-1 min-h-0'>
          <MaterialReactTable table={table} />
        </div>

        {infoMessage ? (
          <Alert severity='warning' sx={{ py: 0 }}>{infoMessage}</Alert>
        ) : null}

        <div className='mt-2 flex w-full items-center justify-between gap-3'>
          <Typography variant='body2' color='text.secondary'>
            Save will update this pass and trigger platform updates where needed.
          </Typography>
          <Button onClick={save} variant='contained' disabled={!hasChanges || hasValidationErrors}>
            Save
          </Button>
        </div>
      </div>

      <ActionErrorDialog title="Pass update failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
    </>
  )
}

export default EditForm

const SubtypeValueField = ({ value, onChange, varSubType, onReset, helperText, error, ...props }) => {
  const dateInputRef = useRef(null)
  const dateTimeInputRef = useRef(null)

  const stringValue = value?.toString?.() ?? ""

  if (varSubType === "boolean") {
    return (
      <TextField
        {...props}
        select
        value={stringValue === "" ? "false" : stringValue.toLowerCase()}
        error={error}
        helperText={helperText}
        onChange={event => onChange(event.target.value)}
      >
        <MenuItem value="false">false</MenuItem>
        <MenuItem value="true">true</MenuItem>
      </TextField>
    )
  }

  if (isColourSubType(varSubType)) {
    const pickerColour = normaliseColourInput(stringValue) ?? "#ffffff"

    return (
      <div className='flex flex-col gap-2'>
        <TextField
          {...props}
          value={stringValue}
          error={error}
          helperText={helperText}
          onChange={event => onChange(event.target.value)}
          InputProps={{
            endAdornment: onReset ? (
              <InputAdornment position='end'>
                <IconButton edge='end' size='small' aria-label='Clear value' onClick={onReset}>
                  <CloseIcon fontSize='small' />
                </IconButton>
              </InputAdornment>
            ) : undefined
          }}
        />
        <HexColorPicker color={pickerColour} onChange={onChange} />
      </div>
    )
  }

  if (varSubType === "choice") {
    return (
      <TextField
        {...props}
        select
        value={stringValue}
        error={error}
        helperText={helperText}
        onChange={event => onChange(event.target.value)}
      >
        <MenuItem value="">Select currency</MenuItem>
        {currencies.map(currency => (
          <MenuItem key={currency.code} value={currency.code}>
            {currency.code} - {currency.name}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  const endAdornment = (
    varSubType === "date" || varSubType === "datetime" || onReset
  ) ? (
    <InputAdornment position='end'>
      {varSubType === "date" ? (
        <>
          <IconButton edge='end' size='small' onClick={() => dateInputRef.current?.showPicker?.() ?? dateInputRef.current?.click()}>
            <EventOutlinedIcon fontSize='small' />
          </IconButton>
          <input
            ref={dateInputRef}
            type='date'
            value={toNativeDateValue(stringValue)}
            onChange={event => onChange(fromNativeDateValue(event.target.value))}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            tabIndex={-1}
          />
        </>
      ) : null}

      {varSubType === "datetime" ? (
        <>
          <IconButton edge='end' size='small' onClick={() => dateTimeInputRef.current?.showPicker?.() ?? dateTimeInputRef.current?.click()}>
            <ScheduleOutlinedIcon fontSize='small' />
          </IconButton>
          <input
            ref={dateTimeInputRef}
            type='datetime-local'
            step='1'
            value={toNativeDateTimeValue(stringValue)}
            onChange={event => onChange(fromNativeDateTimeValue(event.target.value))}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            tabIndex={-1}
          />
        </>
      ) : null}

      {onReset ? (
        <IconButton edge='end' size='small' aria-label='Clear value' onClick={onReset}>
          <CloseIcon fontSize='small' />
        </IconButton>
      ) : null}
    </InputAdornment>
  ) : null

  return (
    <TextField
      {...props}
      value={stringValue}
      type={varSubType === "email" ? "email" : varSubType === "phone" ? "tel" : "text"}
      error={error}
      helperText={helperText}
      onChange={event => {
        if (varSubType === "integer" && !isIntegerInput(event.target.value)) return
        if (["number", "money"].includes(varSubType) && !isDecimalInput(event.target.value)) return
        if (varSubType === "choice" && !/^[a-zA-Z]{0,3}$/.test(event.target.value)) return
        onChange(varSubType === "choice" ? event.target.value.toUpperCase() : event.target.value)
      }}
      inputProps={{
        inputMode: ["integer", "number", "money"].includes(varSubType) ? "decimal" : undefined,
        maxLength: varSubType === "choice" ? 3 : undefined,
        pattern: varSubType === "integer"
          ? "-?[0-9]*"
          : ["number", "money"].includes(varSubType)
            ? "-?[0-9]*[.]?[0-9]*"
            : varSubType === "choice"
              ? "[A-Za-z]{3}"
              : undefined
      }}
      InputProps={{ endAdornment }}
      sx={{
        "& .MuiInputBase-input::placeholder, & .MuiInputBase-inputMultiline::placeholder": {
          color: "text.disabled",
          opacity: 1
        },
        ...(props.sx ?? {})
      }}
    />
  )
}
